/* eslint-disable jsx-a11y/label-has-associated-control, lodash/prefer-lodash-method */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isObject } from 'lodash'
import moment from 'moment'
import notify from 'components/atoms/Notify'
import apiPut from 'lib/network/apiPut'
import apiPost from 'lib/network/apiPost'
import apiGet from 'lib/network/apiGet'
import Selection from 'components/molecules/Selection'
import useModal from 'hooks/useModal'
import ChangeCarWizard, { isOldBookingPeriodRateOnly, isNewCarPeriodPriced } from './modals/ChangeCarWizard'
import DeliveryLocationPicker from './modals/DeliveryLocationPicker'
import { getEditInsurance, getExtrasAmount, isExtendedBooking } from './bookingDetailHelpers'
import './editBookingRedesign.css'

const money = n => `${Number(n || 0).toFixed(3)} OMR`
const num = (v, fallback = 0) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}
// Coordinate as a finite number, or undefined (never coerces a missing value to 0 like `num` does).
const asCoord = v => (Number.isFinite(Number(v)) ? Number(v) : undefined)
const unwrap = (value, key) => (isObject(value) ? value?.[key] : value)

const toLocalInput = value => {
  if (!value) return ''
  const m = moment.utc(value)
  return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : ''
}

const formatAsUTC = dateString => {
  if (!dateString) return null
  const momentDate = moment.utc(dateString)
  return momentDate.isValid() ? momentDate.toISOString() : null
}

const readPriceError = resp => (resp?.success && resp?.data && resp.data.error ? String(resp.data.error) : null)

const currentDays = (start, end) => {
  if (!start || !end) return 1
  const ms = new Date(end) - new Date(start)
  if (!Number.isFinite(ms) || ms <= 0) return 1
  const msInDay = 1000 * 60 * 60 * 24
  let days = Math.floor(ms / msInDay)
  const remainingMs = ms % msInDay
  if (remainingMs > 0 && remainingMs / (1000 * 60 * 60) > 3) {
    days += 1
  }
  return days < 1 ? 1 : days
}

const discountValue = (base, type, value) => {
  const raw = type === 'percent' ? (base * value) / 100 : value
  return Math.max(0, Math.min(base, raw))
}

const sumAjarleeTargetDiscountOnList = (listFee, discounts) => {
  let total = 0
  discounts.forEach(row => {
    const v = num(row.value, 0)
    if (v <= 0) return
    if (row.target === 'service') {
      total += discountValue(listFee, row.type, v)
    }
  })
  return total
}

const resolveAjarleeServiceFeeList = (ajarleeFromBooking, ajarleeDiscountedStored, discounts) => {
  const fromBooking = num(ajarleeFromBooking, 0)
  const stored = num(ajarleeDiscountedStored, 0)
  if (stored <= 0) return fromBooking

  const estimateList = Math.max(fromBooking, stored)
  const discountOnList = sumAjarleeTargetDiscountOnList(estimateList, discounts)
  if (discountOnList > 0) return Math.max(fromBooking, stored + discountOnList)

  return Math.max(fromBooking, stored)
}

const getAjarleeDiscountBreakdownItems = (listFee, discounts) => {
  let running = listFee
  const items = []
  discounts.forEach(row => {
    const v = num(row.value, 0)
    if (v <= 0) return
    if (row.target === 'service') {
      const amount = discountValue(running, row.type, v)
      running = Math.max(0, running - amount)
      if (amount > 0) items.push({ ...row, amount })
    }
  })
  return items
}

const applyAjarleeServiceFeeDiscounts = (listFee, discounts) => {
  let running = listFee
  discounts.forEach(row => {
    const v = num(row.value, 0)
    if (v <= 0) return
    if (row.target === 'service') {
      running = Math.max(0, running - discountValue(running, row.type, v))
    }
  })
  return running
}

let discountRowId = 0
const makeRow = initial => ({
  id: (discountRowId += 1),
  target: initial.target || 'daily',
  source: initial.source || 'Ajrlee',
  type: initial.type || 'percent',
  value: initial.value ?? 0,
})

const TARGET_LABELS = {
  daily: 'Daily rate discount',
  delivery: 'Delivery discount',
  service: 'Service fee discount',
  total: 'Total price discount',
}
const toOptions = items => items.map(item => ({ id: item, name: item }))
const BOOKING_STATUS_OPTIONS = [
  { id: 'pending', name: 'Pending' },
  { id: 'confirmed', name: 'Confirmed' },
  { id: 'cancelled', name: 'Cancelled' },
  { id: 'rejected', name: 'Rejected' },
  { id: 'completed', name: 'Completed' },
  { id: 'failedPayment', name: 'Failed Payment' },
]
const STATUS_NOTIFY_OPTIONS = ['confirmed', 'rejected', 'cancelled']
const DELIVERY_TYPE_OPTIONS = toOptions(['Deliver to me', 'Pickup myself'])
const PICKUP_TIME_WINDOW_OPTIONS = toOptions(['09:00 PM - 10:00 PM', '10:00 AM - 12:00 PM', '02:00 PM - 04:00 PM'])
const DRIVER_ASSIGNMENT_OPTIONS = toOptions(['Assign later', 'Airport driver team', 'Office staff'])
// `id` is the capitalised form of the API value (form.paymentStatus is seeded that way and handleSave
// lowercases it back), so ids must round-trip to the PaymentStatus enum: paid / unpaid / partialpaid /
// failure. Without the partialpaid entry the dropdown had no match for a partial-paid booking and fell
// back to showing the placeholder.
const PAYMENT_STATUS_OPTIONS = [
  { id: 'Paid', name: 'Paid' },
  { id: 'Unpaid', name: 'Unpaid' },
  { id: 'Partialpaid', name: 'Partial paid' },
  { id: 'Failure', name: 'Failure' },
]
const PAYMENT_METHOD_OPTIONS = toOptions(['Cash on delivery', 'Card', 'Bank transfer', 'Wallet'])
const INVOICE_VISIBILITY_OPTIONS = toOptions(['Send after save', 'Keep internal draft', 'Send manually later'])
const DISCOUNT_TARGET_OPTIONS = [
  { id: 'daily', name: 'Daily rate' },
  { id: 'delivery', name: 'Delivery cost' },
  { id: 'service', name: 'Service fee' },
]
const DISCOUNT_SOURCE_OPTIONS = toOptions(['Ajrlee', 'Company'])
const DISCOUNT_TYPE_OPTIONS = [
  { id: 'percent', name: '%' },
  { id: 'fixed', name: 'OMR' },
]

const EditBookingRedesign = ({ bookingData: bookingDataProp = {}, id, refetch, saveRef, readOnly = false }) => {
  const history = useHistory()
  const { t } = useTranslation()
  const summaryRef = useRef(null)

  const lastFetchSigRef = useRef(null)

  const listSnapCacheRef = useRef({ sig: null, snap: null })
  const [changeCarWizardSessionKey, setChangeCarWizardSessionKey] = useState(0)
  const [loadingCarId] = useState(null)
  const [stagedCarSwap, setStagedCarSwap] = useState(null)
  const [stagedCarPricing, setStagedCarPricing] = useState(null)
  const [deliveryDetailsEdited, setDeliveryDetailsEdited] = useState(false)

  const deliveryBoundary = useMemo(() => {
    const b = bookingDataProp?.branch
    const lat = Number(b?.lat)
    const lng = Number(b?.lon)
    const km = Number(b?.deliveryKmRange)
    if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(km) && km > 0) {
      return { center: { lat, lng }, radiusKm: km }
    }
    return null
  }, [bookingDataProp?.branch])

  const bookingData = useMemo(() => {
    const car = stagedCarSwap?.car
    if (!car) return bookingDataProp
    const p = stagedCarSwap.payload || {}

    const snap = stagedCarSwap.delivery ? { ...(stagedCarPricing || {}), ...stagedCarSwap.delivery } : stagedCarPricing
    const snapNum = v => (Number.isFinite(Number(v)) ? Number(v) : undefined)
    const newDailyRate = num(p.dailyRate, num(car.dailyRate, num(bookingDataProp?.dailyRate, 0)))
    const newDailyDiscountRate = num(p.dailyDiscountRate, 0)
    // Same rule as the API's insuranceTypeForCar: keep the booked type only if the new car offers it.
    const bookedInsurance = bookingDataProp?.insuranceType || 'NONE'
    const keepsInsurance = bookedInsurance === 'STANDARD' ? !!snap?.isStandardInsuranceActive : bookedInsurance === 'FULL' ? !!snap?.isFullInsuranceActive : false
    return {
      ...bookingDataProp,
      rentalCar: car,
      insuranceType: keepsInsurance ? bookedInsurance : 'NONE',
      insuranceUnitPrice: keepsInsurance ? snapNum(snap.insuranceUnitPrice) : undefined,
      isInsuranceIncludedInBookingPayment: keepsInsurance ? !!snap.isInsuranceIncludedInBookingPayment : undefined,
      insuranceFee: 0,
      dailyRate: snap ? num(snap.dailyRate, newDailyRate) : newDailyRate,
      rentalCarDailyRate: num(car.dailyRate, newDailyRate),
      dailyDiscountRate: snap ? num(snap.dailyDiscountRate, newDailyDiscountRate) : newDailyDiscountRate,
      vatTaxPercentage: snap ? num(snap.vatTaxPercentage, num(bookingDataProp?.vatTaxPercentage, 0)) : num(bookingDataProp?.vatTaxPercentage, 0),
      officeDeliveryCost: snap ? num(snap.officeDeliveryCost, 0) : num(bookingDataProp?.officeDeliveryCost, 0),
      ajarleeDeliveryCost: snap ? num(snap.ajarleeDeliveryCost, 0) : num(bookingDataProp?.ajarleeDeliveryCost, 0),
      // Stored totals come from the fresh snapshot; while it loads they stay unset so the fallback recomputes.
      rentalCarDiscountPrice: snap ? snapNum(snap.priceWithDays) : undefined,
      priceWithoutTax: snap ? snapNum(snap.amountWithoutInsurance) : undefined,
      vatTax: snap ? snapNum(snap.vatTaxWithoutInsurance) : undefined,
      totalPrice: snap ? snapNum(snap.totalAmountWithoutInsurance) : undefined,
      // The new car carries no prior per-item delivery discount baselines.
      officeDiscountedDeliveryCost: undefined,
      ajarleeDiscountedDeliveryCost: undefined,
      delivery: {
        ...bookingDataProp?.delivery,
        deliveryCost: snap ? num(snap.deliveryCost, 0) : num(bookingDataProp?.delivery?.deliveryCost, 0),
      },
    }
  }, [bookingDataProp, stagedCarSwap, stagedCarPricing])

  const carTitle = useMemo(() => {
    const car = bookingData?.rentalCar
    const make = unwrap(car?.car?.make, 'make') || ''
    const model = unwrap(car?.car?.model, 'modal') || ''
    const year = car?.modelYear ? ` ${car.modelYear}` : ''
    return `${make} ${model}${year}`.trim() || 'Hyundai Elantra 2025'
  }, [bookingData])

  const stagedCarTitle = useMemo(() => {
    const car = stagedCarSwap?.car
    if (!car) return ''
    const make = unwrap(car?.car?.make, 'make') || ''
    const model = unwrap(car?.car?.model, 'modal') || ''
    const year = car?.modelYear ? ` ${car.modelYear}` : ''
    return `${make} ${model}${year}`.trim()
  }, [stagedCarSwap])
  const displayedCarTitle = stagedCarTitle || carTitle

  const companyName = useMemo(() => {
    const rc = bookingData?.rentalCar
    const c = Array.isArray(rc?.company) ? rc?.company?.[0] : rc?.company
    const direct = c?.name || c?.fullName || c?.companyName || rc?.companyName
    if (direct) return direct
    const newCompanyId = c?._id || c
    const oldCompany = bookingDataProp?.rentalCar?.company
    const oldCompanyId = oldCompany?._id || oldCompany
    if (newCompanyId && oldCompanyId && String(newCompanyId) === String(oldCompanyId)) return oldCompany?.name || '—'
    return '—'
  }, [bookingData, bookingDataProp])
  const branchName = useMemo(() => {
    const rc = bookingData?.rentalCar
    const b = Array.isArray(rc?.branch) ? rc?.branch?.[0] : rc?.branch
    return b?.name || b?.branchName || rc?.branchName || bookingData?.branch?.name || '—'
  }, [bookingData])
  const branchAddress = useMemo(() => {
    const rc = bookingData?.rentalCar
    const b = Array.isArray(rc?.branch) ? rc?.branch?.[0] : rc?.branch
    return b?.address || bookingData?.branch?.address || branchName
  }, [bookingData, branchName])

  const [activeTab, setActiveTab] = useState('booking')
  const [isSaving, setIsSaving] = useState(false)
  const [sendStatusNotification, setSendStatusNotification] = useState(false)
  const [pricingError, setPricingError] = useState(null)
  const [stagedPricingError, setStagedPricingError] = useState(null)

  const [form, setForm] = useState({
    bookingStatus: bookingData?.status || 'confirmed',
    bookingSource: bookingData?.bookingSource || 'Admin panel',
    customerName: bookingData?.user?.fullName || 'Ali Mohammed',
    phone: bookingData?.user?.phone || '+964 783 887 2294',
    email: bookingData?.user?.email || 'alidargzli@gmail.com',
    adminNote: bookingData?.adminNote || '',
    rentalStart: toLocalInput(bookingData?.rentalStartDate) || '2026-07-08T21:30',
    rentalEnd: toLocalInput(bookingData?.rentalEndDate) || '2026-07-11T00:00',
    dailyRate: num(bookingData?.dailyRate, 14.9),
    deliveryType: bookingData?.delivery?.type === 'PICK_UP_MYSELF' ? 'Pickup myself' : 'Deliver to me',
    deliveryCost: num(bookingData?.delivery?.deliveryCost, 3),
    pickupAddress: bookingData?.delivery?.pickupLocationAddress || bookingData?.pickupLocationAddress || '',
    dropoffAddress: bookingData?.delivery?.dropOffLocationAddress || bookingData?.dropOffLocationAddress || '',
    pickupLat: bookingData?.delivery?.pickupLocation?.coordinates?.[1] ?? bookingData?.pickupLat ?? null,
    pickupLon: bookingData?.delivery?.pickupLocation?.coordinates?.[0] ?? bookingData?.pickupLon ?? null,
    dropoffLat: bookingData?.delivery?.dropOffLocation?.coordinates?.[1] ?? bookingData?.dropoffLat ?? null,
    dropoffLon: bookingData?.delivery?.dropOffLocation?.coordinates?.[0] ?? bookingData?.dropoffLon ?? null,
    deliveryInstructions: bookingData?.deliveryInstructions || '',
    flightNumber: bookingData?.flightNumber || '',
    paymentStatus: bookingData?.paymentStatus ? bookingData.paymentStatus.charAt(0).toUpperCase() + bookingData.paymentStatus.slice(1) : 'Unpaid',
    paymentMethod: 'Cash on delivery',
    serviceFee: num(bookingData?.ajarleeDeliveryCost, 2),
    vatRate: num(bookingData?.vatTaxPercentage, 5),
    paidAmount: (() => {
      const status = String(bookingData?.paymentStatus || '').toLowerCase()
      const total = num(bookingData?.totalPrice, 0)
      if (status === 'paid') return total
      if (status === 'partialpaid') {
        const payable = num(bookingData?.paymentSummary?.payableAmount ?? bookingData?.payableAmount, 0)
        return num(bookingData?.paidAmount, Math.max(0, total - payable))
      }
      return num(bookingData?.paidAmount, 0)
    })(),
    invoiceVisibility: 'Send after save',
    pickupTimeWindow: '09:00 PM - 10:00 PM',
    driverAssignment: 'Assign later',
    reasonNote: bookingData?.discountReasonNote || '',
    insuranceType: bookingData?.insuranceType || 'NONE',
  })

  const [discounts, setDiscounts] = useState(() => [])
  const [hasUserEditedDiscounts, setHasUserEditedDiscounts] = useState(false)
  const discountsSeededRef = useRef(false)

  useEffect(() => {
    setHasUserEditedDiscounts(false)
    discountsSeededRef.current = false
    setSendStatusNotification(false)
  }, [id])

  useEffect(() => {
    if (hasUserEditedDiscounts || discountsSeededRef.current) return

    const dailyRateFromBooking = num(bookingData?.dailyRate, 0)
    const dailyDiscountRateFromBooking = num(bookingData?.dailyDiscountRate, 0)
    const bakedInDailyDiscount =
      dailyRateFromBooking > 0 && dailyDiscountRateFromBooking > 0 && dailyDiscountRateFromBooking < dailyRateFromBooking
        ? Math.max(0, dailyRateFromBooking - dailyDiscountRateFromBooking)
        : 0
    const bakedInDailySource = String(bookingData?.discountSource || '')
      .toLowerCase()
      .includes('ajarlee')
      ? 'Ajrlee'
      : 'Company'
    const makeBakedInDailyRow = () => makeRow({ target: 'daily', source: bakedInDailySource, type: 'fixed', value: bakedInDailyDiscount })

    if (!bookingData?.isDiscountEdit && bookingData?.isDiscountEdit !== true) {
      if (bakedInDailyDiscount > 0) {
        setDiscounts([makeBakedInDailyRow()])
        discountsSeededRef.current = true
      }
      return
    }

    if (Array.isArray(bookingData?.discountControl)) {
      const seeded = bookingData.discountControl
        .map(row => {
          const target = row?.target
          if (!target) return null
          return makeRow({
            target,
            source: row?.source || 'Ajrlee',
            type: row?.type || 'fixed',
            value: row?.value ?? 0,
          })
        })
        .filter(Boolean)

      if (bakedInDailyDiscount > 0 && !seeded.some(r => r.target === 'daily')) {
        seeded.unshift(makeBakedInDailyRow())
      }
      setDiscounts(seeded)
      discountsSeededRef.current = true
      return
    }

    const seedRows = []

    // Daily rate discount: original dailyRate vs dailyDiscountRate (computed above as bakedInDailyDiscount).
    if (bakedInDailyDiscount > 0) {
      seedRows.push(makeBakedInDailyRow())
    }

    const officeDeliveryOriginal = num(bookingData?.officeDeliveryCost, 0)
    const officeDeliveryDiscounted = num(bookingData?.officeDiscountedDeliveryCost, officeDeliveryOriginal)
    if (officeDeliveryOriginal > 0 && officeDeliveryDiscounted >= 0 && officeDeliveryDiscounted < officeDeliveryOriginal) {
      const deliveryDiscountFixed = Math.max(0, officeDeliveryOriginal - officeDeliveryDiscounted)
      if (deliveryDiscountFixed > 0) {
        seedRows.push(
          makeRow({
            target: 'delivery',
            source: 'Company',
            type: 'fixed',
            value: deliveryDiscountFixed,
          })
        )
      }
    }

    const ajarleeDeliveryOriginal = num(bookingData?.ajarleeDeliveryCost, 0)
    const ajarleeDeliveryDiscounted = num(bookingData?.ajarleeDiscountedDeliveryCost, ajarleeDeliveryOriginal)
    if (ajarleeDeliveryOriginal > 0 && ajarleeDeliveryDiscounted >= 0 && ajarleeDeliveryDiscounted < ajarleeDeliveryOriginal) {
      const serviceDiscountFixed = Math.max(0, ajarleeDeliveryOriginal - ajarleeDeliveryDiscounted)
      if (serviceDiscountFixed > 0) {
        seedRows.push(
          makeRow({
            target: 'service',
            source: 'Ajrlee',
            type: 'fixed',
            value: serviceDiscountFixed,
          })
        )
      }
    }

    if (seedRows.length > 0) {
      setDiscounts(seedRows)
      discountsSeededRef.current = true
    }
  }, [bookingData, hasUserEditedDiscounts])

  const isPaidBooking = useMemo(() => String(form.paymentStatus || bookingData?.paymentStatus || '').toLowerCase() === 'paid', [form.paymentStatus, bookingData?.paymentStatus])
  // Discount control center is only available for strictly unpaid bookings.
  const isUnpaidBooking = useMemo(() => String(form.paymentStatus || bookingData?.paymentStatus || '').toLowerCase() === 'unpaid', [form.paymentStatus, bookingData?.paymentStatus])
  const paidCarSwapDiscountEditable = isPaidBooking && Boolean(stagedCarSwap)
  const discountsReadOnly = !(isUnpaidBooking || paidCarSwapDiscountEditable)
  const showDiscountCard = isUnpaidBooking || paidCarSwapDiscountEditable || discounts.length > 0
  const savedPaymentStatus = String(bookingData?.paymentStatus || '').toLowerCase()
  const canEditDeliveryType = savedPaymentStatus === 'paid' || savedPaymentStatus === 'unpaid'

  const wizardBookingData = useMemo(() => {
    const rentalStartDate = form.rentalStart ? moment(form.rentalStart).toISOString() : bookingData?.rentalStartDate
    const rentalEndDate = form.rentalEnd ? moment(form.rentalEnd).toISOString() : bookingData?.rentalEndDate

    return {
      ...bookingData,
      dailyRate: bookingDataProp?.dailyRate,
      dailyDiscountRate: bookingDataProp?.dailyDiscountRate,
      rentalCarDailyRate: bookingDataProp?.rentalCarDailyRate,
      isDiscountEdit: bookingDataProp?.isDiscountEdit,
      deliveryType: bookingData?.delivery?.type || bookingData?.deliveryType,
      rentalStartDate,
      rentalEndDate,
      pickupLocationAddress: form.pickupAddress || bookingData?.delivery?.pickupLocationAddress || bookingData?.pickupLocationAddress,
      dropOffLocationAddress: form.dropoffAddress || bookingData?.delivery?.dropOffLocationAddress || bookingData?.dropOffLocationAddress,
      pickupLat: bookingData?.pickupLat,
      pickupLon: bookingData?.pickupLon,
      dropoffLat: bookingData?.dropoffLat,
      dropoffLon: bookingData?.dropoffLon,
      lat: bookingData?.lat,
      lon: bookingData?.lon,
    }
  }, [bookingData, bookingDataProp, form.rentalStart, form.rentalEnd, form.pickupAddress, form.dropoffAddress])

  // Car change is allowed for confirmed/pending bookings (paid or unpaid); blocked for extended bookings.
  const canChangeCar = !readOnly && (form.bookingStatus === 'confirmed' || form.bookingStatus === 'pending') && !isExtendedBooking(bookingData)

  const [ChangeCarModal, openChangeCarModal, closeChangeCarModal] = useModal({
    content: (
      <ChangeCarWizard
        key={changeCarWizardSessionKey}
        bookingData={wizardBookingData}
        bookingId={id}
        loadingCarId={loadingCarId}
        onSelectCar={() => {}}
        onStageCarSwap={swap => {
          setStagedCarSwap(swap)
          const p = swap?.payload || {}

          // Paid booking + "change price": the old booking's discounts belong to the old car and must not
          // follow it. Start from a clean sheet on the new car's own rate so the admin enters fresh rows.
          // (Unpaid keeps its existing behaviour — the branches below are untouched.)
          if (isPaidBooking && swap?.priceDecision === 'change') {
            setField('dailyRate', num(p.dailyRate, num(swap?.car?.dailyRate, 0)))
            setDiscounts([])
            setHasUserEditedDiscounts(true)
            closeChangeCarModal()
            return
          }

          const periodNoDiscount = isOldBookingPeriodRateOnly(bookingDataProp) && p.dailyRate == null && !p.dailyDiscountRate
          if (periodNoDiscount) {
            setDiscounts(prev => prev.filter(r => r.target !== 'daily'))
            setHasUserEditedDiscounts(true)
            closeChangeCarModal()
            return
          }
          const isChangePrice = swap?.priceDecision === 'change'
          const existingDailyRow = discounts.find(r => r.target === 'daily')

          const oldHasAdminDailyDiscount = Array.isArray(bookingDataProp?.discountControl) && bookingDataProp.discountControl.some(r => r?.target === 'daily' && num(r?.value) > 0)

          if (isChangePrice && existingDailyRow && existingDailyRow.type !== 'percent' && oldHasAdminDailyDiscount) {
            setField('dailyRate', num(swap?.car?.dailyRate, num(p.dailyRate, 0)))
            setHasUserEditedDiscounts(true)
            closeChangeCarModal()
            return
          }
          const newDailyRate = num(p.dailyRate, num(swap?.car?.dailyRate, 0))
          setField('dailyRate', newDailyRate)

          const effectiveDaily = num(p.dailyDiscountRate, 0)
          const perDayDailyDiscount = effectiveDaily > 0 && effectiveDaily < newDailyRate ? Math.round((newDailyRate - effectiveDaily) * 1000) / 1000 : 0
          const stagedDailySource = String(p.discountSource || '')
            .toLowerCase()
            .includes('ajarlee')
            ? 'Ajrlee'
            : 'Company'
          setDiscounts(prev => {
            const existingDaily = prev.find(r => r.target === 'daily')
            const others = prev.filter(r => r.target !== 'daily')
            if (isChangePrice && existingDaily && existingDaily.type === 'percent') return prev
            if (perDayDailyDiscount <= 0) return others
            if (existingDaily && existingDaily.type === 'percent') {
              const pct = Math.round((perDayDailyDiscount / newDailyRate) * 100 * 1000) / 1000
              return [makeRow({ target: 'daily', source: existingDaily.source || stagedDailySource, type: 'percent', value: pct }), ...others]
            }
            return [makeRow({ target: 'daily', source: existingDaily?.source || stagedDailySource, type: 'fixed', value: perDayDailyDiscount }), ...others]
          })
          setHasUserEditedDiscounts(true)
          closeChangeCarModal()
        }}
        onCancelSuccess={newBookingId => {
          closeChangeCarModal()
          if (newBookingId && String(newBookingId) !== String(id)) {
            notify.success(t('updateBooking.messages.bookingCancelledAndNewCreated') || 'Booking cancelled and new booking created successfully')
            history.push(`/booking/edit/${newBookingId}`)
          } else {
            notify.success('Booking updated successfully')
            if (refetch) refetch()
          }
        }}
        isAdmin
      />
    ),
    title: t('updateBooking.labels.changeCar') || 'Change Car',
    dimensions: {
      height: '650px',
      width: '950px',
    },
  })

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

  const handleBookingStatusChange = selected => {
    const nextStatus = selected?.id || ''
    if (!nextStatus || nextStatus === form.bookingStatus) return

    const originalStatus = bookingData?.status
    const isNotifyStatus = STATUS_NOTIFY_OPTIONS.includes(nextStatus)
    const statusActuallyChanged = nextStatus !== originalStatus

    if (isNotifyStatus && statusActuallyChanged) {
      notify.alertConfirm({
        title: 'Send status notification?',
        message: 'Do you want to send the user a notification (email and WhatsApp) about this status update?',
        dangerText: 'Yes',
        cancelText: 'No',
        onConfirm: () => {
          setField('bookingStatus', nextStatus)
          setSendStatusNotification(true)
        },
        onCancel: () => {
          setField('bookingStatus', nextStatus)
          setSendStatusNotification(false)
        },
      })
      return
    }

    setField('bookingStatus', nextStatus)
    setSendStatusNotification(false)
  }

  const [PickupMapModal, openPickupMap, closePickupMap] = useModal({
    title: t('updateBooking.labels.pickupAddress') || 'Pickup location',
    dimensions: { height: '660px', width: '760px' },
    content: (
      <DeliveryLocationPicker
        initialLat={asCoord(form.pickupLat)}
        initialLon={asCoord(form.pickupLon)}
        boundaryCenter={deliveryBoundary?.center}
        boundaryRadiusKm={deliveryBoundary?.radiusKm}
        onClose={() => closePickupMap()}
        onLocationSelect={loc => {
          setField('pickupAddress', loc.address)
          setField('pickupLat', loc.lat)
          setField('pickupLon', loc.lon)
          setDeliveryDetailsEdited(true)
          closePickupMap()
        }}
      />
    ),
  })

  const [DropoffMapModal, openDropoffMap, closeDropoffMap] = useModal({
    title: t('updateBooking.labels.dropoffAddress') || 'Drop-off location',
    dimensions: { height: '660px', width: '760px' },
    content: (
      <DeliveryLocationPicker
        initialLat={asCoord(form.dropoffLat)}
        initialLon={asCoord(form.dropoffLon)}
        boundaryCenter={deliveryBoundary?.center}
        boundaryRadiusKm={deliveryBoundary?.radiusKm}
        onClose={() => closeDropoffMap()}
        onLocationSelect={loc => {
          setField('dropoffAddress', loc.address)
          setField('dropoffLat', loc.lat)
          setField('dropoffLon', loc.lon)
          setDeliveryDetailsEdited(true)
          closeDropoffMap()
        }}
      />
    ),
  })

  const addDiscount = initial => {
    const target = initial?.target
    if (target && discounts.some(r => r.target === target)) {
      notify.error(`A ${TARGET_LABELS[target] || target} is already added — you can't add it twice`)
      return
    }
    if (num(initial?.value, 0) > 0) setHasUserEditedDiscounts(true)
    const normalized = initial?.target === 'service' ? { ...initial, source: 'Ajrlee' } : initial
    setDiscounts(prev => [...prev, makeRow(normalized)])
  }
  const removeDiscount = rowId => {
    // Removing an empty (value 0) row changes nothing price-wise, so don't flag an edit / trigger a fetch.
    const removed = discounts.find(r => r.id === rowId)
    if (num(removed?.value, 0) > 0) setHasUserEditedDiscounts(true)
    setDiscounts(prev => prev.filter(r => r.id !== rowId))
  }
  const updateDiscount = (rowId, patch) => {
    if (patch.target && discounts.some(r => r.id !== rowId && r.target === patch.target)) {
      notify.error(`A ${TARGET_LABELS[patch.target] || patch.target} is already added — you can't add it twice`)
      return
    }
    setHasUserEditedDiscounts(true)
    setDiscounts(prev =>
      prev.map(r => {
        if (r.id !== rowId) return r
        const merged = { ...r, ...patch }
        if (merged.target === 'service') merged.source = 'Ajrlee'
        return merged
      })
    )
  }
  const isPickupSelf = form.deliveryType !== 'Deliver to me'
  const availableTargetOptions = isPickupSelf ? DISCOUNT_TARGET_OPTIONS.filter(opt => opt.id !== 'delivery' && opt.id !== 'service') : DISCOUNT_TARGET_OPTIONS
  const targetOptionsForRow = row => {
    const opts = availableTargetOptions.filter(opt => opt.id === row.target || !discounts.some(r => r.id !== row.id && r.target === opt.id))
    if (row.target === 'total' && !opts.some(o => o.id === 'total')) {
      return [{ id: 'total', name: 'Total price' }, ...opts]
    }
    return opts
  }
  const firstFreeTarget = () => availableTargetOptions.find(opt => !discounts.some(r => r.target === opt.id))
  // Service-fee discounts belong to Ajrlee only — hide Company from the Source dropdown for that target.
  const sourceOptionsForRow = row => (row.target === 'service' ? DISCOUNT_SOURCE_OPTIONS.filter(o => o.id === 'Ajrlee') : DISCOUNT_SOURCE_OPTIONS)

  const days = currentDays(form.rentalStart, form.rentalEnd)

  const dateRangeInvalid = Boolean(form.rentalStart && form.rentalEnd && new Date(form.rentalEnd) <= new Date(form.rentalStart))

  const bookingStartTs = bookingData?.rentalStartDate ? moment(bookingData.rentalStartDate).valueOf() : null
  const bookingEndTs = bookingData?.rentalEndDate ? moment(bookingData.rentalEndDate).valueOf() : null
  const currentStartTs = form.rentalStart ? moment(form.rentalStart).valueOf() : null
  const currentEndTs = form.rentalEnd ? moment(form.rentalEnd).valueOf() : null

  const hasDateChanged =
    bookingStartTs != null && bookingEndTs != null && currentStartTs != null && currentEndTs != null ? currentStartTs !== bookingStartTs || currentEndTs !== bookingEndTs : false

  const deliverToMeSelected = form.deliveryType === 'Deliver to me'
  const savedPickupLat = asCoord(bookingData?.delivery?.pickupLocation?.coordinates?.[1])
  const savedPickupLon = asCoord(bookingData?.delivery?.pickupLocation?.coordinates?.[0])
  const savedIsDeliverToMe = (bookingData?.delivery?.type || '') === 'DELIVER_TO_ME'
  const hasPickedPickupCoords = asCoord(form.pickupLat) != null && asCoord(form.pickupLon) != null
  const pickupLocationChanged =
    hasPickedPickupCoords &&
    (savedPickupLat == null || savedPickupLon == null || Math.abs(num(form.pickupLat) - savedPickupLat) > 1e-6 || Math.abs(num(form.pickupLon) - savedPickupLon) > 1e-6)

  const shouldRepriceDelivery = deliverToMeSelected && hasPickedPickupCoords && (pickupLocationChanged || !savedIsDeliverToMe || hasDateChanged)

  const [deliveryReprice, setDeliveryReprice] = useState(null)

  useEffect(() => {
    const rentalCarId = bookingData?.rentalCar?._id
    if (!shouldRepriceDelivery || !rentalCarId || !form.rentalStart || !form.rentalEnd) {
      setDeliveryReprice(null)
      return undefined
    }

    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const resp = await apiGet(
          'vehicles/prices',
          {
            rentalCarId,
            bookingFrom: moment(form.rentalStart).format('YYYY-MM-DD HH:mm:ss'),
            bookingTo: moment(form.rentalEnd).format('YYYY-MM-DD HH:mm:ss'),
            lat: asCoord(form.pickupLat),
            lon: asCoord(form.pickupLon),
          },
          false
        )
        if (cancelled) return
        const data = resp?.success && !readPriceError(resp) ? resp?.data || null : null
        setDeliveryReprice(data && data.officeDeliveryCost !== undefined ? data : null)
      } catch (e) {
        if (!cancelled) setDeliveryReprice(null)
      }
    }, 350)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [shouldRepriceDelivery, bookingData?.rentalCar?._id, form.rentalStart, form.rentalEnd, form.pickupLat, form.pickupLon])

  const officeListFeeBase = num(deliveryReprice?.officeDeliveryCost, num(bookingData?.officeDeliveryCost, num(form.deliveryCost, 0)))
  const serviceListFeeBase = num(deliveryReprice?.ajarleeDeliveryCost, num(bookingData?.ajarleeDeliveryCost, num(form.serviceFee, 0)))

  const calc = useMemo(() => {
    const dailyRate = num(form.dailyRate, 0)
    const delivery = officeListFeeBase
    const componentRows = discounts.filter(r => r.target !== 'total')
    const totalRows = discounts.filter(r => r.target === 'total')
    const serviceFeeList = deliveryReprice ? serviceListFeeBase : resolveAjarleeServiceFeeList(serviceListFeeBase, bookingData?.ajarleeDiscountedDeliveryCost, componentRows)
    const serviceFee = serviceFeeList
    const vatRate = num(form.vatRate, 0)
    const paidAmount = num(form.paidAmount, 0)

    const rentSubtotal = dailyRate * days
    const originalVatTaxable = rentSubtotal + delivery
    const originalPriceBeforeVat = originalVatTaxable + serviceFee
    const originalVat = (originalVatTaxable * vatRate) / 100
    const originalTotal = originalPriceBeforeVat + originalVat

    let rentAfter = rentSubtotal
    let deliveryAfter = delivery
    let serviceAfter = serviceFee

    let ajrleeDiscount = 0
    let officeDiscount = 0
    const breakdown = []

    let serviceRunning = serviceFeeList

    componentRows.forEach(row => {
      let amount = 0
      if (row.target === 'daily') {
        const perDayAmount = discountValue(dailyRate, row.type, num(row.value, 0))
        amount = perDayAmount * days
        rentAfter = Math.max(0, rentAfter - amount)
      } else if (row.target === 'delivery') {
        amount = discountValue(delivery, row.type, num(row.value, 0))
        deliveryAfter = Math.max(0, deliveryAfter - amount)
      } else if (row.target === 'service') {
        amount = discountValue(serviceRunning, row.type, num(row.value, 0))
        serviceRunning = Math.max(0, serviceRunning - amount)
        serviceAfter = serviceRunning
      }
      if (row.source === 'Ajrlee') ajrleeDiscount += amount
      else officeDiscount += amount
      if (amount > 0) breakdown.push({ ...row, amount })
    })

    const priceBeforeVat = rentAfter + deliveryAfter + serviceAfter
    const vatTaxable = rentAfter + deliveryAfter
    let totalPriceDiscount = 0
    const vat = (vatTaxable * vatRate) / 100
    const totalBeforeTotalDiscount = priceBeforeVat + vat
    totalRows.forEach(row => {
      const amount = discountValue(Math.max(0, totalBeforeTotalDiscount - totalPriceDiscount), row.type, num(row.value, 0))
      totalPriceDiscount += amount
      if (row.source === 'Ajrlee') ajrleeDiscount += amount
      else officeDiscount += amount
      if (amount > 0) breakdown.push({ ...row, amount })
    })

    const taxableAfter = vatTaxable
    const finalTotal = Math.max(0, totalBeforeTotalDiscount - totalPriceDiscount)
    const savings = originalTotal - finalTotal
    const balanceDue = Math.max(0, finalTotal - paidAmount)

    const totalDiscount = ajrleeDiscount + officeDiscount
    const ajrleePct = totalDiscount ? (ajrleeDiscount / totalDiscount) * 100 : 0
    const officePct = totalDiscount ? (officeDiscount / totalDiscount) * 100 : 0

    return {
      rentSubtotal,
      rentAfter,
      delivery,
      deliveryAfter,
      serviceFee,
      serviceAfter,
      vatRate,
      paidAmount,
      originalTotal,
      taxableAfter,
      vat,
      finalTotal,
      savings,
      balanceDue,
      breakdown,
      ajrleeDiscount,
      officeDiscount,
      ajrleePct,
      officePct,
      totalDiscount,
    }
  }, [form, discounts, days, officeListFeeBase, serviceListFeeBase, deliveryReprice, bookingData?.ajarleeDiscountedDeliveryCost])

  const dailyDiscountInfo = useMemo(() => {
    const dailyRate = num(form.dailyRate, 0)
    if (!dailyRate || !days) {
      return {
        dailyDiscountRateForApi: undefined,
        discountSource: undefined,
        discountAmountPerDay: 0,
        totalDailyDiscount: 0,
        totalDailyDiscountByAjarlee: 0,
      }
    }

    let totalDailyDiscountByAjarlee = 0
    let totalDailyDiscountByOffice = 0

    discounts
      .filter(d => d.target === 'daily' && (d.source === 'Ajrlee' || d.source === 'Company'))
      .forEach(row => {
        const amount = discountValue(dailyRate, row.type, num(row.value, 0))
        if (amount <= 0) return
        if (row.source === 'Ajrlee') totalDailyDiscountByAjarlee += amount
        else totalDailyDiscountByOffice += amount
      })

    const totalDailyDiscount = totalDailyDiscountByAjarlee + totalDailyDiscountByOffice
    if (totalDailyDiscount <= 0) {
      return {
        dailyDiscountRateForApi: undefined,
        discountSource: undefined,
        discountAmountPerDay: 0,
        totalDailyDiscount: 0,
        totalDailyDiscountByAjarlee: 0,
      }
    }

    const dailyDiscountRateForApi = Math.max(0, dailyRate - totalDailyDiscount)
    const discountAmountPerDay = Math.max(0, dailyRate - dailyDiscountRateForApi)

    const discountSource =
      totalDailyDiscountByAjarlee > 0 && totalDailyDiscountByOffice === 0
        ? 'ajarlee'
        : totalDailyDiscountByOffice > 0 && totalDailyDiscountByAjarlee === 0
        ? 'company'
        : totalDailyDiscountByAjarlee > 0
        ? 'ajarlee'
        : 'company'

    return {
      dailyDiscountRateForApi,
      discountSource,
      discountAmountPerDay,
      totalDailyDiscount,
      totalDailyDiscountByAjarlee,
    }
  }, [form.dailyRate, days, discounts])

  const [pricingOriginal, setPricingOriginal] = useState(null)
  const [pricingWithDailyDiscount, setPricingWithDailyDiscount] = useState(null)
  const [pricingLoading, setPricingLoading] = useState(false)

  const skipDeliveryForApi = useMemo(() => {
    return form.deliveryType !== 'Deliver to me'
  }, [form.deliveryType])

  useEffect(() => {
    const carId = stagedCarSwap?.car?._id
    if (!carId || !form.rentalStart || !form.rentalEnd) {
      setStagedCarPricing(null)
      setStagedPricingError(null)
      return undefined
    }
    const bookingFrom = moment(form.rentalStart).format('YYYY-MM-DD HH:mm:ss')
    const bookingTo = moment(form.rentalEnd).format('YYYY-MM-DD HH:mm:ss')
    let cancelled = false
    const run = async () => {
      try {
        const resp = await apiGet(
          'vehicles/prices',
          { rentalCarId: carId, bookingFrom, bookingTo, skipDelivery: skipDeliveryForApi, insuranceType: bookingDataProp?.insuranceType || 'NONE' },
          false
        )
        if (!cancelled) {
          const apiError = readPriceError(resp)
          setStagedPricingError(apiError)
          const data = resp?.success && !apiError ? resp?.data || null : null
          setStagedCarPricing(data)
          const periodSwap = isOldBookingPeriodRateOnly(bookingDataProp) || isNewCarPeriodPriced(stagedCarSwap?.car)
          if (data && periodSwap && !discounts.some(r => r.target === 'daily')) {
            setField('dailyRate', num(data.dailyRate, num(stagedCarSwap?.car?.dailyRate, 0)))
          }
        }
      } catch (e) {
        if (!cancelled) {
          setStagedCarPricing(null)
          setStagedPricingError(null)
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [stagedCarSwap, form.rentalStart, form.rentalEnd, skipDeliveryForApi])

  const deliveryDiscountInfo = useMemo(() => {
    const officeListFee = officeListFeeBase

    const ajarleeFromApi = serviceListFeeBase
    const ajarleeDiscountedStored = num(bookingData?.ajarleeDiscountedDeliveryCost, 0)
    // A re-quote for a moved pickup location wins over the (location-agnostic) list snapshot.
    const ajarleeListFee =
      num(deliveryReprice?.ajarleeDeliveryCost) > 0
        ? num(deliveryReprice.ajarleeDeliveryCost)
        : num(pricingOriginal?.ajarleeDeliveryCost) > 0
        ? num(pricingOriginal.ajarleeDeliveryCost)
        : resolveAjarleeServiceFeeList(ajarleeFromApi, ajarleeDiscountedStored, discounts)

    let officeDiscountAmount = 0
    discounts.forEach(row => {
      const v = num(row.value, 0)
      if (v <= 0 || row.target !== 'delivery') return
      officeDiscountAmount += discountValue(officeListFee, row.type, v)
    })

    const rawOfficeDiscountedDeliveryCost = Math.max(0, officeListFee - officeDiscountAmount)
    const rawAjarleeDiscountedDeliveryCost = applyAjarleeServiceFeeDiscounts(ajarleeListFee, discounts)

    const hasOfficeDiscountInput = discounts.some(row => row.target === 'delivery' && Number(row.value) > 0)
    const hasAjarleeDiscountInput = discounts.some(row => row.target === 'service' && Number(row.value) > 0)

    const officeDiscountedDeliveryCostForApi = hasOfficeDiscountInput && rawOfficeDiscountedDeliveryCost < officeListFee ? rawOfficeDiscountedDeliveryCost : undefined
    const ajarleeDiscountedDeliveryCostForApi = hasAjarleeDiscountInput && rawAjarleeDiscountedDeliveryCost < ajarleeListFee ? rawAjarleeDiscountedDeliveryCost : undefined

    const hasDeliveryDiscountApplied =
      !skipDeliveryForApi && ((officeDiscountedDeliveryCostForApi != null && officeListFee > 0) || (ajarleeDiscountedDeliveryCostForApi != null && ajarleeListFee > 0))

    return {
      hasDeliveryDiscountApplied,
      officeDiscountedDeliveryCostForApi,
      ajarleeDiscountedDeliveryCostForApi,
      officeListFee,
      ajarleeListFee,
    }
  }, [
    discounts,
    officeListFeeBase,
    serviceListFeeBase,
    bookingData?.ajarleeDiscountedDeliveryCost,
    skipDeliveryForApi,
    deliveryReprice?.ajarleeDeliveryCost,
    pricingOriginal?.officeDeliveryCost,
    pricingOriginal?.ajarleeDeliveryCost,
  ])

  const hasRateChanged = bookingData?.dailyRate != null ? num(form.dailyRate) !== num(bookingData?.dailyRate) : false
  const hasDailyDiscountApplied = dailyDiscountInfo.discountAmountPerDay > 0 && dailyDiscountInfo.dailyDiscountRateForApi != null
  const hasServiceFeeDiscountApplied = discounts.some(row => row.target === 'service' && num(row.value, 0) > 0)
  const hasComponentDiscountRows = discounts.some(row => row.target !== 'total' && num(row.value, 0) > 0)
  const hasTotalDiscountRowsForFetch = discounts.some(row => row.target === 'total' && num(row.value, 0) > 0)

  const originalSkipDelivery = (bookingData?.delivery?.type || '') !== 'DELIVER_TO_ME'
  const hasDeliveryTypeChanged = skipDeliveryForApi !== originalSkipDelivery

  const bookingHadSavedDiscount =
    num(bookingData?.dailyDiscountRate, 0) > 0 ||
    bookingData?.officeDiscountedDeliveryCost != null ||
    bookingData?.ajarleeDiscountedDeliveryCost != null ||
    bookingData?.isDiscountEdit === true

  const shouldFetchPrices =
    hasUserEditedDiscounts &&
    Boolean(bookingData?.rentalCar?._id && form.rentalStart && form.rentalEnd) &&
    (hasRateChanged || hasDateChanged || hasComponentDiscountRows || hasTotalDiscountRowsForFetch || hasDeliveryTypeChanged || bookingHadSavedDiscount)

  const deliveryId = bookingData?.deliveryId || bookingData?.delivery?._id || null
  const switchingIntoDelivery = !skipDeliveryForApi && (bookingData?.delivery?.type || '') !== 'DELIVER_TO_ME'

  const buildBasePriceParams = (bookingFromFormatted, bookingToFormatted, rentalCarId) => {
    const params = {
      rentalCarId,
      bookingFrom: bookingFromFormatted,
      bookingTo: bookingToFormatted,
      skipDelivery: skipDeliveryForApi,
    }

    if (!skipDeliveryForApi && shouldRepriceDelivery) {
      params.lat = asCoord(form.pickupLat)
      params.lon = asCoord(form.pickupLon)
    }
    return params
  }

  const buildDiscountedPriceParams = (baseParams, listSnap) => {
    const params = { ...baseParams }
    const listOffice = num(listSnap?.officeDeliveryCost, officeListFeeBase)
    const listAjarlee = num(listSnap?.ajarleeDeliveryCost, serviceListFeeBase)

    const bookingDailyDiscountRate = num(bookingData?.dailyDiscountRate, 0)
    const apiDailyDiscountRate =
      hasDailyDiscountApplied && dailyDiscountInfo.dailyDiscountRateForApi != null
        ? Number(dailyDiscountInfo.dailyDiscountRateForApi)
        : (hasRateChanged || hasDateChanged) && bookingDailyDiscountRate > 0
        ? bookingDailyDiscountRate
        : undefined

    if (apiDailyDiscountRate != null && hasDailyDiscountApplied) {
      params.dailyDiscountRate = apiDailyDiscountRate
    } else if (bookingDailyDiscountRate > 0 && bookingDailyDiscountRate < num(form.dailyRate, 0)) {
      params.dailyDiscountRate = num(form.dailyRate, 0)
    }

    const hasOfficeDiscountInput = discounts.some(row => row.target === 'delivery' && Number(row.value) > 0)
    const hasAjarleeDiscountInput = discounts.some(row => row.target === 'service' && Number(row.value) > 0)

    if (hasOfficeDiscountInput || hasAjarleeDiscountInput) {
      params.isDeliveryDiscount = true
      if (hasOfficeDiscountInput) {
        let officeRunning = listOffice
        discounts.forEach(row => {
          const v = num(row.value, 0)
          if (v <= 0 || row.target !== 'delivery') return
          officeRunning = Math.max(0, officeRunning - discountValue(listOffice, row.type, v))
        })
        if (officeRunning < listOffice) params.officeDiscountedDeliveryCost = officeRunning
      }
      if (hasAjarleeDiscountInput) {
        const ajarleeFinal = applyAjarleeServiceFeeDiscounts(listAjarlee, discounts)
        if (ajarleeFinal < listAjarlee) params.ajarleeDiscountedDeliveryCost = ajarleeFinal
      }
    }

    return params
  }

  const meaningfulDiscountsSig = useMemo(() => JSON.stringify(discounts.filter(r => num(r.value, 0) > 0).map(r => [r.target, r.source, r.type, num(r.value, 0)])), [discounts])

  useEffect(() => {
    const rentalCarId = bookingData?.rentalCar?._id
    if (!rentalCarId) return
    if (!form.rentalStart || !form.rentalEnd) return

    const bookingFromFormatted = moment(form.rentalStart).format('YYYY-MM-DD HH:mm:ss')
    const bookingToFormatted = moment(form.rentalEnd).format('YYYY-MM-DD HH:mm:ss')

    if (!bookingFromFormatted || !bookingToFormatted) return

    if (!shouldFetchPrices) {
      setPricingError(null)
      lastFetchSigRef.current = null
      if (!hasUserEditedDiscounts) {
        setPricingOriginal(null)
        setPricingWithDailyDiscount(null)
      } else {
        setPricingWithDailyDiscount(null)
      }
      return
    }

    let cancelled = false
    const run = async () => {
      setPricingLoading(true)
      try {
        const baseParams = buildBasePriceParams(bookingFromFormatted, bookingToFormatted, rentalCarId)

        if (cancelled) return

        const listCacheSig = JSON.stringify([rentalCarId, bookingFromFormatted, bookingToFormatted, skipDeliveryForApi, baseParams.lat ?? null, baseParams.lon ?? null])
        let listSnap = listSnapCacheRef.current.sig === listCacheSig ? listSnapCacheRef.current.snap : null

        if (!listSnap) {
          const listResp = await apiGet('vehicles/prices', baseParams, false)
          if (!listResp?.success || !listResp?.data) {
            if (!cancelled) {
              setPricingOriginal(null)
              setPricingWithDailyDiscount(null)
            }
            return
          }

          const listError = readPriceError(listResp)
          if (listError) {
            if (!cancelled) {
              setPricingError(listError)
              setPricingOriginal(null)
              setPricingWithDailyDiscount(null)
            }
            return
          }

          listSnap = listResp.data
          listSnapCacheRef.current = { sig: listCacheSig, snap: listSnap }
        }
        if (!cancelled) setPricingError(null)

        const datesActuallyChanged = form.rentalStart !== toLocalInput(bookingData?.rentalStartDate) || form.rentalEnd !== toLocalInput(bookingData?.rentalEndDate)
        if (!cancelled && !stagedCarSwap && datesActuallyChanged) {
          const snapDaily = num(listSnap.dailyRate, 0)
          if (snapDaily > 0 && Math.abs(snapDaily - num(form.dailyRate, 0)) > 1e-6) {
            setField('dailyRate', snapDaily)
          }
        }
        const discountedParams = buildDiscountedPriceParams(baseParams, listSnap)
        const hasDiscountParams = discountedParams.dailyDiscountRate != null || discountedParams.isDeliveryDiscount === true

        if (hasDiscountParams) {
          const discountedResp = await apiGet('vehicles/prices', discountedParams, false)
          if (!cancelled) {
            setPricingOriginal(listSnap)
            setPricingWithDailyDiscount(discountedResp?.success ? discountedResp?.data || null : listSnap)
          }
        } else if (!cancelled) {
          setPricingOriginal(listSnap)
          setPricingWithDailyDiscount(null)
        }
      } catch (e) {
        if (!cancelled) {
          setPricingOriginal(null)
          setPricingWithDailyDiscount(null)
        }
      } finally {
        if (!cancelled) setPricingLoading(false)
      }
    }

    const fetchSig = JSON.stringify([
      rentalCarId,
      bookingFromFormatted,
      bookingToFormatted,
      skipDeliveryForApi,
      deliveryId,
      meaningfulDiscountsSig,
      num(form.dailyRate),
      hasDailyDiscountApplied ? num(dailyDiscountInfo.dailyDiscountRateForApi) : null,
      officeListFeeBase,
      serviceListFeeBase,
      shouldRepriceDelivery ? [asCoord(form.pickupLat) ?? null, asCoord(form.pickupLon) ?? null] : null,
    ])

    const debounceTimer = setTimeout(() => {
      if (fetchSig === lastFetchSigRef.current) return
      lastFetchSigRef.current = fetchSig
      run()
    }, 450)
    return () => {
      cancelled = true
      clearTimeout(debounceTimer)
      setPricingLoading(false)
    }
  }, [
    bookingData?.rentalCar?._id,
    bookingData?.dailyRate,
    bookingData?.dailyDiscountRate,
    bookingData?.rentalStartDate,
    bookingData?.rentalEndDate,
    officeListFeeBase,
    serviceListFeeBase,
    form.rentalStart,
    form.rentalEnd,
    form.dailyRate,
    form.serviceFee,
    form.pickupLat,
    form.pickupLon,
    shouldRepriceDelivery,
    skipDeliveryForApi,
    deliveryId,
    meaningfulDiscountsSig,
    hasDailyDiscountApplied,
    dailyDiscountInfo.dailyDiscountRateForApi,
    hasRateChanged,
    hasDateChanged,
    hasComponentDiscountRows,
    hasTotalDiscountRowsForFetch,
    hasDeliveryTypeChanged,
    shouldFetchPrices,
    hasUserEditedDiscounts,
  ])

  const bookingRentPerDay = useMemo(() => {
    const dailyDiscountRate = num(bookingData?.dailyDiscountRate, 0)
    const dailyRate = num(bookingData?.dailyRate, 0)
    if ((dailyDiscountRate || dailyRate) === 0 && bookingData?.rentalCarDailyRate != null) return num(bookingData?.rentalCarDailyRate, 0)
    return dailyDiscountRate > 0 ? dailyDiscountRate : dailyRate
  }, [bookingData, form.dailyRate])

  const fallbackPricing = useMemo(() => {
    const vatTaxPercentage = num(bookingData?.vatTaxPercentage, num(form.vatRate, 0))
    return {
      dailyRate: num(bookingData?.dailyRate, num(form.dailyRate, 0)),
      dailyDiscountRate: num(bookingData?.dailyDiscountRate, 0),
      priceWithDays: num(bookingData?.rentalCarDiscountPrice, num(bookingRentPerDay, 0) * days),
      officeDeliveryCost: num(bookingData?.officeDiscountedDeliveryCost, num(bookingData?.officeDeliveryCost, 0)),
      ajarleeDeliveryCost: num(bookingData?.ajarleeDiscountedDeliveryCost, num(bookingData?.ajarleeDeliveryCost, 0)),
      amountWithoutInsurance: num(bookingData?.priceWithoutTax, num(bookingData?.amountWithoutInsurance, 0)),
      vatTaxWithoutInsurance: num(bookingData?.vatTax, num(bookingData?.vatTaxWithoutInsurance, 0)),
      vatTaxPercentage,
      totalAmountWithoutInsurance: num(bookingData?.totalPrice, num(bookingData?.totalAmountWithoutInsurance, 0)),
    }
  }, [bookingData, bookingRentPerDay, days, form.dailyRate, form.vatRate])

  const pricingFinal = hasUserEditedDiscounts ? pricingWithDailyDiscount || pricingOriginal || fallbackPricing : fallbackPricing

  const hasTotalDiscountRows = useMemo(() => discounts.some(row => row.target === 'total' && num(row.value, 0) > 0), [discounts])

  const vatPctForDisplay = Number(pricingFinal?.vatTaxPercentage ?? form.vatRate) || 0

  const isFullDailyDiscount = Number(dailyDiscountInfo.dailyDiscountRateForApi) === 0 && num(dailyDiscountInfo.discountAmountPerDay, 0) > 0
  const displayRentSubtotal = isFullDailyDiscount ? 0 : hasDailyDiscountApplied ? num(dailyDiscountInfo.dailyDiscountRateForApi) * days : num(pricingFinal?.priceWithDays)

  const displayDelivery = useMemo(() => {
    if (skipDeliveryForApi) return 0
    const officeOriginal = officeListFeeBase
    let office = officeOriginal
    discounts.forEach(row => {
      if (row.target !== 'delivery') return
      const v = num(row.value, 0)
      if (v > 0) office = Math.max(0, office - discountValue(officeOriginal, row.type, v))
    })
    return office
  }, [skipDeliveryForApi, officeListFeeBase, discounts])
  const displayServiceFee = useMemo(() => {
    if (skipDeliveryForApi) return 0
    const ajarleeOriginal = num(deliveryDiscountInfo.ajarleeListFee, serviceListFeeBase)
    return applyAjarleeServiceFeeDiscounts(ajarleeOriginal, discounts)
  }, [skipDeliveryForApi, deliveryDiscountInfo.ajarleeListFee, serviceListFeeBase, discounts])

  const round3 = n => Math.round((Number(n) || 0) * 1000) / 1000
  const extraRows = (bookingData?.extras || []).map(extra => ({ ...extra, amount: round3(num(extra.pricePerDay) * days) }))
  const displayExtras = extraRows.length ? round3(extraRows.reduce((sum, extra) => sum + extra.amount, 0)) : getExtrasAmount(bookingData)
  // A staged car change decides insurance itself (see bookingData above); otherwise the admin's pick.
  const insurance = getEditInsurance(bookingData, stagedCarSwap ? bookingData?.insuranceType || 'NONE' : form.insuranceType, days)
  const insuranceDroppedBySwap = !!stagedCarSwap && !!stagedCarPricing && (bookingDataProp?.insuranceType || 'NONE') !== 'NONE' && insurance.type === 'NONE'
  const insuranceTypeLabel = type => (type === 'STANDARD' ? t('booking.orderSummary.insuranceStandard') : type === 'FULL' ? t('booking.orderSummary.insuranceFull') : 'None')
  // NONE + the car's active tiers; the booked type stays selectable even if the car has since disabled it.
  const insuranceOptions = ['NONE', 'STANDARD', 'FULL']
    .filter(
      type =>
        type === 'NONE' ||
        type === (bookingData?.insuranceType || 'NONE') ||
        (type === 'STANDARD' ? bookingData?.rentalCar?.isStandardInsuranceActive : bookingData?.rentalCar?.isFullInsuranceActive)
    )
    .map(type => ({ id: type, name: insuranceTypeLabel(type) }))
  const displayTaxableAfter = displayRentSubtotal + displayDelivery + displayExtras + insurance.fee
  const displayVat = round3((displayTaxableAfter * vatPctForDisplay) / 100)
  const componentInvoiceTotal = round3(displayRentSubtotal + displayDelivery + displayExtras + insurance.fee + displayServiceFee + displayVat)

  const discountErrors = useMemo(() => {
    const errors = {}
    const dailyBase = num(form.dailyRate, 0)
    const officeBase = officeListFeeBase
    const serviceBase = num(deliveryDiscountInfo.ajarleeListFee, serviceListFeeBase)
    const rawAmount = (base, type, value) => (type === 'percent' ? (base * num(value, 0)) / 100 : num(value, 0))

    discounts.forEach(row => {
      const v = num(row.value, 0)
      if (v <= 0) return
      if (row.type === 'percent' && v > 100) {
        errors[row.id] = 'Percentage cannot exceed 100%'
        return
      }
      if (row.target === 'daily') {
        if (rawAmount(dailyBase, row.type, v) > dailyBase) errors[row.id] = `Daily rate discount can't exceed the daily rate (${money(dailyBase)})`
      } else if (row.target === 'delivery') {
        if (rawAmount(officeBase, row.type, v) > officeBase) errors[row.id] = `Delivery discount can't exceed the delivery cost (${money(officeBase)})`
      } else if (row.target === 'service') {
        if (rawAmount(serviceBase, row.type, v) > serviceBase) errors[row.id] = `Service fee discount can't exceed the service fee (${money(serviceBase)})`
      } else if (row.target === 'total') {
        if (rawAmount(componentInvoiceTotal, row.type, v) > componentInvoiceTotal) errors[row.id] = `Total price discount can't exceed the total (${money(componentInvoiceTotal)})`
      }
    })
    return errors
  }, [discounts, form.dailyRate, officeListFeeBase, serviceListFeeBase, deliveryDiscountInfo.ajarleeListFee, componentInvoiceTotal])

  const hasDiscountErrors = Object.keys(discountErrors).length > 0

  const deliveryCostDisplay = skipDeliveryForApi ? 0 : displayDelivery

  const totalDiscountBreakdown = useMemo(() => {
    const items = []
    let running = componentInvoiceTotal
    let totalDiscount = 0
    discounts
      .filter(r => r.target === 'total' && num(r.value, 0) > 0)
      .forEach(row => {
        const amount = discountValue(running, row.type, num(row.value, 0))
        totalDiscount += amount
        running = Math.max(0, running - amount)
        if (amount > 0) items.push({ ...row, amount })
      })
    return { totalDiscount, items }
  }, [discounts, componentInvoiceTotal])

  const displayFinalTotal = round3(Math.max(0, componentInvoiceTotal - totalDiscountBreakdown.totalDiscount))

  const displayOriginalTotal = (() => {
    if (skipDeliveryForApi) {
      const rent = num(form.dailyRate, num(bookingData?.dailyRate, 0)) * days
      return round3(rent + displayExtras + insurance.fee + round3(((rent + displayExtras + insurance.fee) * vatPctForDisplay) / 100))
    }
    const rent = num(form.dailyRate, num(bookingData?.dailyRate, 0)) * days
    const office = officeListFeeBase
    const service = num(deliveryDiscountInfo.ajarleeListFee, serviceListFeeBase)
    const vat = round3(((rent + office + displayExtras + insurance.fee) * vatPctForDisplay) / 100)
    return round3(rent + office + displayExtras + insurance.fee + service + vat)
  })()
  const displaySavings = Math.max(0, round3(displayOriginalTotal - displayFinalTotal))

  const displayRentPerDay = (() => {
    if (isFullDailyDiscount) return 0
    if (hasDailyDiscountApplied) return num(dailyDiscountInfo.dailyDiscountRateForApi)
    const discounted = num(pricingFinal?.dailyDiscountRate, 0)
    if (discounted > 0) return discounted
    const base = num(pricingFinal?.dailyRate, 0)
    if (base > 0) return base
    return num(form.dailyRate) || bookingRentPerDay
  })()

  const displayBreakdown = useMemo(() => {
    const ajarleeListFee = num(deliveryDiscountInfo.ajarleeListFee, serviceListFeeBase)

    const rentDiff =
      !isFullDailyDiscount && !hasDailyDiscountApplied && pricingOriginal && pricingWithDailyDiscount
        ? Math.max(0, num(pricingOriginal.priceWithDays) - num(pricingWithDailyDiscount.priceWithDays))
        : discounts
            .filter(r => r.target === 'daily' && num(r.value, 0) > 0)
            .reduce((sum, row) => sum + discountValue(num(form.dailyRate, 0), row.type, num(row.value, 0)) * days, 0)

    const officeDiff = skipDeliveryForApi
      ? 0
      : discounts.filter(r => r.target === 'delivery' && num(r.value, 0) > 0).reduce((sum, row) => sum + discountValue(officeListFeeBase, row.type, num(row.value, 0)), 0)

    const splitByWeight = (rows, totalAmount) => {
      const weight = rows.reduce((sum, r) => sum + num(r.value, 0), 0)
      if (weight <= 0 || totalAmount <= 0) return []
      return rows.map(r => ({ ...r, amount: (totalAmount * num(r.value, 0)) / weight }))
    }

    const ajarleeItems = skipDeliveryForApi ? [] : getAjarleeDiscountBreakdownItems(ajarleeListFee, discounts)

    const dailyRows = discounts.filter(r => r.target === 'daily' && num(r.value, 0) > 0)
    // All delivery-cost discounts (any source) — they reduce the office delivery cost.
    const officeRows = skipDeliveryForApi ? [] : discounts.filter(r => r.target === 'delivery' && num(r.value, 0) > 0)

    return [...splitByWeight(dailyRows, rentDiff), ...splitByWeight(officeRows, officeDiff), ...ajarleeItems].filter(i => i.amount > 0)
  }, [
    pricingOriginal,
    pricingWithDailyDiscount,
    discounts,
    deliveryDiscountInfo.ajarleeListFee,
    officeListFeeBase,
    serviceListFeeBase,
    form.dailyRate,
    days,
    isFullDailyDiscount,
    hasDailyDiscountApplied,
    skipDeliveryForApi,
  ])

  const allDisplayBreakdown = useMemo(() => [...displayBreakdown, ...totalDiscountBreakdown.items], [displayBreakdown, totalDiscountBreakdown.items])

  const apiSourceSplit = useMemo(() => {
    let ajrleeDiscount = 0
    let officeDiscount = 0
    allDisplayBreakdown.forEach(item => {
      if (item.source === 'Ajrlee') ajrleeDiscount += item.amount
      else officeDiscount += item.amount
    })
    const totalDiscount = ajrleeDiscount + officeDiscount
    return {
      ajrleeDiscount,
      officeDiscount,
      ajrleePct: totalDiscount ? (ajrleeDiscount / totalDiscount) * 100 : 0,
      officePct: totalDiscount ? (officeDiscount / totalDiscount) * 100 : 0,
    }
  }, [allDisplayBreakdown])

  const paymentStatusNorm = String(form.paymentStatus || bookingData?.paymentStatus || '').toLowerCase()
  const savedPaymentStatusNorm = String(bookingDataProp?.paymentStatus || '').toLowerCase()
  const existingPartialPayable =
    paymentStatusNorm === 'partialpaid' || savedPaymentStatusNorm === 'partialpaid' ? num(bookingDataProp?.paymentSummary?.payableAmount ?? bookingDataProp?.payableAmount, 0) : 0

  // Amount the customer already paid on the saved booking. Must NOT track the live
  // recalculated total — during a staged car swap `bookingData.totalPrice` is the NEW
  // car total, while the customer still only paid the original booking total.
  const paidAmount = (() => {
    const savedTotal = num(bookingDataProp?.totalPrice, 0)
    const apiPaid = bookingDataProp?.paidAmount
    if (apiPaid != null && Number.isFinite(Number(apiPaid)) && Number(apiPaid) >= 0) {
      if (savedPaymentStatusNorm === 'paid' || savedPaymentStatusNorm === 'partialpaid') {
        return num(apiPaid, savedTotal)
      }
    }
    if (savedPaymentStatusNorm === 'paid') return savedTotal
    if (savedPaymentStatusNorm === 'partialpaid') {
      return Math.max(0, round3(savedTotal - existingPartialPayable))
    }
    return num(form.paidAmount, num(bookingData?.paidAmount, 0))
  })()

  // Paid-booking delivery-change deltas — baseline is what was already collected, not the live total.
  const originalPaidTotal = paidAmount
  const deliverSelected = form.deliveryType === 'Deliver to me'
  const isPaidNow = String(form.paymentStatus).toLowerCase() === 'paid'

  const deliveryPayable = isPaidNow && deliverSelected && hasDeliveryTypeChanged ? Math.max(0, round3(Number(displayFinalTotal) - originalPaidTotal)) : 0
  const deliveryWalletRefund = isPaidNow && !deliverSelected && hasDeliveryTypeChanged ? Math.max(0, round3(originalPaidTotal - Number(displayFinalTotal))) : 0

  // Paid-booking car-swap deltas, same treatment as the delivery change above: a dearer new car leaves the
  // customer owing the difference (booking becomes partial paid), a cheaper one is refunded to their wallet.
  // `bookingData.totalPrice` is overlaid with the staged car's total while a swap is pending, so the amount
  // actually paid has to come from the untouched prop — the same baseline the API settles against.
  const carSwapPaidTotal = num(bookingDataProp?.totalPrice, paidAmount)
  const carSwapDelta = stagedCarSwap && isPaidNow ? round3(Number(displayFinalTotal) - carSwapPaidTotal) : 0
  const carSwapPayable = carSwapDelta > 0 ? carSwapDelta : 0
  const carSwapWalletRefund = carSwapDelta < 0 ? Math.abs(carSwapDelta) : 0

  const livePayableDelta = Math.max(deliveryPayable, carSwapPayable)
  const displayBalanceDue = Math.max(0, livePayableDelta > 0 ? livePayableDelta : existingPartialPayable > 0 ? existingPartialPayable : Number(displayFinalTotal) - paidAmount)

  const discountAppliedInDisplay = allDisplayBreakdown.length > 0

  const discountSourceLabel = (() => {
    const raw = dailyDiscountInfo?.discountSource || bookingData?.discountSource
    const s = String(raw || '').toLowerCase()
    if (s.includes('ajarlee')) return 'Ajrlee'
    if (s.includes('company')) return 'Company'
    return 'Ajrlee/Company'
  })()

  // Writes every discount field onto `payload`. Shared by the normal `PUT booking/:id` save (unpaid) and by
  // the `POST booking/cancel/:id` car swap of a PAID booking, where the discount has to travel with the swap
  // itself: that endpoint creates the new booking AND reconciles the money against what the customer already
  // paid, so it must see the final discounted total in the same request.
  const applyDiscountPayload = payload => {
    const discountRowsToSave = skipDeliveryForApi ? discounts.filter(r => r.target !== 'delivery' && r.target !== 'service') : discounts
    if (Array.isArray(discountRowsToSave) && (discountRowsToSave.length > 0 || bookingHadSavedDiscount)) {
      payload.isDiscountEdit = true
      payload.discountControl = discountRowsToSave.map(row => ({
        target: row.target,
        source: row.source,
        type: row.type,
        value: num(row.value),
      }))
    }

    if (dailyDiscountInfo.dailyDiscountRateForApi != null && dailyDiscountInfo.discountAmountPerDay > 0) {
      payload.isDiscountEdit = true
      payload.dailyDiscountRate = num(dailyDiscountInfo.dailyDiscountRateForApi)
      payload.discountSource = dailyDiscountInfo.discountSource
      payload.discountAmount = num(dailyDiscountInfo.discountAmountPerDay)
    } else if (!(stagedCarSwap && isOldBookingPeriodRateOnly(bookingDataProp)) && (discounts.some(r => r.target === 'daily') || num(bookingData?.dailyDiscountRate, 0) > 0)) {
      payload.isDiscountEdit = true
      payload.dailyDiscountRate = 0
      payload.discountAmount = 0
      payload.discountSource = 'none'
    }

    if (deliveryDiscountInfo.hasDeliveryDiscountApplied) {
      payload.isDiscountEdit = true
      if (deliveryDiscountInfo.officeDiscountedDeliveryCostForApi != null) {
        // Active office (Company) delivery discount — persist both the list cost and the discounted cost.
        payload.officeDeliveryCost = num(deliveryDiscountInfo.officeListFee)
        payload.officeDiscountedDeliveryCost = num(deliveryDiscountInfo.officeDiscountedDeliveryCostForApi)
      }
      if (deliveryDiscountInfo.ajarleeDiscountedDeliveryCostForApi != null) {
        payload.ajarleeDeliveryCost = num(deliveryDiscountInfo.ajarleeListFee)
        payload.ajarleeDiscountedDeliveryCost = num(deliveryDiscountInfo.ajarleeDiscountedDeliveryCostForApi)
      }
    }

    const hadOfficeDeliveryDiscount = discounts.some(r => r.target === 'delivery') || bookingData?.officeDiscountedDeliveryCost != null
    if (!skipDeliveryForApi && deliveryDiscountInfo.officeDiscountedDeliveryCostForApi == null && hadOfficeDeliveryDiscount) {
      payload.isDiscountEdit = true
      payload.officeDeliveryCost = num(deliveryDiscountInfo.officeListFee)
      payload.officeDiscountedDeliveryCost = 0
    }

    if (hasServiceFeeDiscountApplied && deliveryDiscountInfo.ajarleeDiscountedDeliveryCostForApi != null) {
      payload.isDiscountEdit = true

      payload.ajarleeDeliveryCost = num(deliveryDiscountInfo.ajarleeListFee)
      payload.ajarleeDiscountedDeliveryCost = num(deliveryDiscountInfo.ajarleeDiscountedDeliveryCostForApi)
    }

    const hadServiceFeeDiscount = discounts.some(r => r.target === 'service') || bookingData?.ajarleeDiscountedDeliveryCost != null
    if (!skipDeliveryForApi && deliveryDiscountInfo.ajarleeDiscountedDeliveryCostForApi == null && hadServiceFeeDiscount) {
      payload.isDiscountEdit = true
      payload.ajarleeDeliveryCost = num(deliveryDiscountInfo.ajarleeListFee)
      payload.ajarleeDiscountedDeliveryCost = 0
    }

    if (hasTotalDiscountRows) {
      payload.isDiscountEdit = true
      payload.discountedTotalPrice = num(displayFinalTotal)
    }

    if (stagedCarSwap && !skipDeliveryForApi) {
      payload.isDiscountEdit = true
      payload.officeDeliveryCost = officeListFeeBase
      payload.ajarleeDeliveryCost = serviceListFeeBase
    }

    return payload
  }

  const handleSave = async () => {
    if (!id) {
      notify.error('Booking id not available')
      return
    }
    if (hasDiscountErrors) {
      notify.error('Please fix the discount validation errors before saving')
      return
    }
    if (dateRangeInvalid) {
      notify.error('End date must be after the start date')
      return
    }
    const priceUnavailableError = pricingError || stagedPricingError
    if (priceUnavailableError) {
      notify.error(priceUnavailableError)
      return
    }

    setIsSaving(true)
    try {
      if (stagedCarSwap) {
        const swapPayload = { ...stagedCarSwap.payload }
        if (isPaidBooking) {
          // A paid swap cancels this booking and creates a new one, then settles the difference against the
          // amount already paid — so the discount must be part of THIS request, not a follow-up update.
          applyDiscountPayload(swapPayload)
          // The wizard omits `dailyRate` for a period-priced swap so the backend applies the new car's own
          // weekly/monthly rate — don't force one in that case.
          if (stagedCarSwap.payload?.dailyRate != null) {
            swapPayload.dailyRate = num(form.dailyRate)
          }
          swapPayload.discountReasonNote = form.reasonNote || ''
          // The new booking is cloned from this one, so it inherits `discountControl`. Always send the row
          // set explicitly (empty on "change price") so the old car's rows can never survive the swap.
          if (!Array.isArray(swapPayload.discountControl)) {
            swapPayload.isDiscountEdit = true
            swapPayload.discountControl = []
          }
        }
        const swapResponse = await apiPost(`booking/cancel/${id}`, swapPayload)
        if (!swapResponse?.success) {
          notify.error(swapResponse?.message || 'Failed to update booking')
          return
        }
        const newBookingId = swapResponse?.data?.newBookingId || null
        if (newBookingId && String(newBookingId) !== String(id)) {
          // Paid car change created a new booking (with the discount above already applied) → open its Edit screen.
          setStagedCarSwap(null)
          notify.success(t('updateBooking.messages.bookingCancelledAndNewCreated') || 'Booking cancelled and new booking created successfully')
          history.push(`/booking/edit/${newBookingId}`)
          return
        }
      }

      const isPaid = String(form.paymentStatus).toLowerCase() === 'paid'
      const payload = {
        status: form.bookingStatus,
        paymentStatus: String(form.paymentStatus).toLowerCase(),
        dailyRate: num(form.dailyRate),
        rentalCar: bookingData?.rentalCar?._id,
        adminNote: form.adminNote || '',
        discountReasonNote: form.reasonNote || '',
        deliveryInstructions: form.deliveryInstructions || '',
        flightNumber: form.flightNumber?.trim() ? form.flightNumber.trim().toUpperCase() : '',
      }

      if (sendStatusNotification) {
        payload.sendStatusNotification = true
      }

      if (!isPaidBooking) {
        applyDiscountPayload(payload)
        if (form.insuranceType !== (bookingData?.insuranceType || 'NONE')) {
          payload.insuranceType = form.insuranceType
        }
      }

      if (!isPaid) {
        payload.rentalStartDate = formatAsUTC(form.rentalStart)
        payload.rentalEndDate = formatAsUTC(form.rentalEnd)

        if (hasDateChanged && !hasDeliveryTypeChanged) {
          payload.numberOfDays = days
        }
      }

      const deliverToMe = form.deliveryType === 'Deliver to me'
      if (hasDeliveryTypeChanged || (deliverToMe && deliveryDetailsEdited)) {
        const pf = pricingFinal || {}

        if (deliverToMe) {
          const delivery = {
            type: 'DELIVER_TO_ME',
            deliveryCost: shouldRepriceDelivery ? round3(displayDelivery + displayServiceFee) : num(pf.deliveryCost, num(form.deliveryCost)),
            pickupLocationAddress: form.pickupAddress,
            dropOffLocationAddress: form.dropoffAddress,
          }
          // Prefer the coordinates picked on the map; otherwise carry forward the existing GeoJSON point.
          const geoFrom = (lat, lon, existing) => {
            const la = asCoord(lat)
            const lo = asCoord(lon)
            if (la != null && lo != null) return { type: 'Point', coordinates: [lo, la] }
            return existing?.coordinates?.length === 2 ? existing : undefined
          }
          const pickupGeo = geoFrom(form.pickupLat, form.pickupLon, bookingData?.delivery?.pickupLocation)
          const dropoffGeo = geoFrom(form.dropoffLat, form.dropoffLon, bookingData?.delivery?.dropOffLocation)
          if (pickupGeo) delivery.pickupLocation = pickupGeo
          if (dropoffGeo) delivery.dropOffLocation = dropoffGeo
          payload.delivery = delivery
        } else {
          payload.delivery = { type: 'PICK_UP_MYSELF' }
        }

        if (deliveryPayable > 0) {
          payload.paymentStatus = 'partialpaid'
        }
      }

      const response = await apiPut(`booking/${id}`, payload)
      if (response?.success) {
        setStagedCarSwap(null)
        setSendStatusNotification(false)
        notify.success('Booking updated')
        if (refetch) refetch()
      }
    } catch (error) {
      notify.error('Failed to update booking')
    } finally {
      setIsSaving(false)
    }
  }

  if (saveRef) saveRef.current = handleSave
  useEffect(() => {
    return () => {
      if (saveRef) saveRef.current = null
    }
    // eslint-disable-next-line
  }, [])

  const statusPillClass = status => {
    const s = String(status).toLowerCase()
    if (s === 'confirmed' || s === 'completed') return 'pill green'
    if (s === 'pending' || s === 'inprogress' || s === 'userreview') return 'pill amber'
    if (s === 'cancelled' || s === 'rejected' || s === 'failedpayment') return 'pill gray'
    return 'pill gray'
  }
  const paymentPillClass = status => {
    const s = String(status).toLowerCase()
    if (s === 'paid') return 'pill green'
    if (s === 'unpaid' || s === 'pending') return 'pill amber'
    return 'pill gray'
  }

  return (
    <div className="eb-redesign">
      {/* Hero */}
      <section className="hero">
        <div className="booking-card">
          <div className="title-row">
            <div>
              <div className="kicker">Edit Booking</div>
              <h1>
                {form.customerName} · {carTitle}
              </h1>
              <p className="sub">All pricing changes are previewed live before update. Every discount must have target, owner and reason.</p>
            </div>
            <div className="status-row">
              <span className={statusPillClass(form.bookingStatus)}>● {form.bookingStatus}</span>
              <span className={paymentPillClass(form.paymentStatus)}>● {form.paymentStatus}</span>
              {(deliveryPayable > 0 || carSwapPayable > 0) && <span className="pill amber">● Partial paid</span>}
              <span className="pill blue">
                {days} {days === 1 ? 'day' : 'days'}
              </span>
            </div>
          </div>
          <div className="facts">
            <div className="fact">
              <span>Customer</span>
              <b>{form.customerName}</b>
            </div>
            <div className="fact">
              <span>Phone</span>
              <b>{form.phone}</b>
            </div>
            <div className="fact">
              <span>Pickup</span>
              <b>{form.pickupAddress}</b>
            </div>
            <div className="fact">
              <span>Drop-off</span>
              <b>{form.dropoffAddress}</b>
            </div>
          </div>
        </div>
      </section>

      <section className="layout">
        <div className="left-col">
          {/* Booking setup */}
          <div className="panel">
            <div className="panel-header">
              <div>
                <h2>Booking setup</h2>
                <div className="hint">Core status, car, dates and delivery details.</div>
              </div>
            </div>

            <div className="tabs" role="tablist">
              {[
                { id: 'booking', label: '1. Booking' },
                { id: 'vehicle', label: '2. Vehicle & dates' },
                { id: 'delivery', label: '3. Delivery' },
              ].map(tab => (
                <button type="button" key={tab.id} className={activeTab === tab.id ? 'tab active' : 'tab'} onClick={() => setActiveTab(tab.id)}>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="tab-panels">
              {/* 1. Booking */}
              <section className={activeTab === 'booking' ? 'tab-panel active' : 'tab-panel'}>
                <div className="form-grid">
                  <div className="field">
                    <label>Booking status</label>
                    <Selection
                      className="w-full eb-select eb-select-normal"
                      list={BOOKING_STATUS_OPTIONS}
                      title="Select booking status"
                      value={form.bookingStatus}
                      onChange={handleBookingStatusChange}
                    />
                  </div>
                  <div className="field">
                    <label>Payment status</label>
                    <Selection
                      className="w-full eb-select eb-select-normal"
                      list={PAYMENT_STATUS_OPTIONS}
                      title="Select payment status"
                      value={form.paymentStatus}
                      onChange={selected => setField('paymentStatus', selected?.id || '')}
                    />
                  </div>
                  <div className="field full">
                    <label>Admin note</label>
                    <textarea rows={3} value={form.adminNote} onChange={e => setField('adminNote', e.target.value)} />
                  </div>
                </div>
                <div className="step-footer">
                  <span className="left-note">Step 1 saves customer and booking status only.</span>
                  <button type="button" className="btn-blue" onClick={() => setActiveTab('vehicle')}>
                    Continue to Vehicle & dates
                  </button>
                </div>
              </section>

              {/* 2. Vehicle & dates */}
              <section className={activeTab === 'vehicle' ? 'tab-panel active' : 'tab-panel'}>
                <div className="option-grid">
                  <div className="option-card active">
                    <strong>Selected car</strong>
                    <span>{displayedCarTitle}</span>
                    <span className={stagedCarTitle ? 'pill blue' : 'pill green'}>{stagedCarTitle ? 'Pending — confirm to apply' : 'Available'}</span>
                  </div>
                  <div className="option-card">
                    <strong>Company</strong>
                    <span>{companyName}</span>
                    <span className="pill blue">Company profile</span>
                  </div>
                  <div className="option-card">
                    <strong>Branch</strong>
                    <span>{branchName || ''}</span>
                    <span className="pill blue">{branchAddress || ''}</span>
                  </div>
                </div>
                <div className="form-grid">
                  <div className="field full">
                    <label>Rental car</label>
                    <div className="input-action">
                      <input className="readonly" value={displayedCarTitle} readOnly />
                      {canChangeCar && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => {
                            setChangeCarWizardSessionKey(k => k + 1)
                            openChangeCarModal()
                          }}
                        >
                          {t('updateBooking.labels.changeCar') || 'Change Car'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="field">
                    <label>Company</label>
                    <input className="readonly" value={companyName} readOnly />
                  </div>
                  <div className="field">
                    <label>Branch</label>
                    <input className="readonly" value={branchName} readOnly />
                  </div>
                  <div className="field">
                    <label>Rental start date</label>
                    <input
                      className={isPaidBooking ? 'readonly' : undefined}
                      type="datetime-local"
                      value={form.rentalStart}
                      onChange={e => {
                        setHasUserEditedDiscounts(true)
                        setField('rentalStart', e.target.value)
                      }}
                      readOnly={isPaidBooking}
                    />
                  </div>
                  <div className="field">
                    <label>Rental end date</label>
                    <input
                      className={isPaidBooking ? 'readonly' : undefined}
                      type="datetime-local"
                      value={form.rentalEnd}
                      onChange={e => {
                        setHasUserEditedDiscounts(true)
                        setField('rentalEnd', e.target.value)
                      }}
                      readOnly={isPaidBooking}
                    />
                    {dateRangeInvalid && <span style={{ color: '#d4380d', fontSize: 12, marginTop: 4, display: 'block' }}>End date must be after the start date.</span>}
                  </div>
                  <div className="field">
                    <label>Charged days</label>
                    <input className="readonly" value={days} readOnly />
                  </div>
                  <div className="field">
                    <label>Daily rate</label>
                    <input className="readonly" type="number" step="0.001" value={form.dailyRate} readOnly />
                  </div>
                  {!isPaidBooking && (
                    <div className="field">
                      <label>Insurance</label>
                      <Selection
                        className="w-full eb-select eb-select-normal"
                        list={insuranceOptions}
                        title="Select insurance"
                        value={form.insuranceType}
                        disabled={!!stagedCarSwap}
                        onChange={selected => setField('insuranceType', selected?.id || 'NONE')}
                      />
                      {insurance.type !== 'NONE' && (
                        <span style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                          {insurance.type === 'FULL' ? `${money(insurance.unitPrice)} / day` : `${money(insurance.unitPrice)} flat`} ·{' '}
                          {insurance.collectAtOffice ? t('booking.orderSummary.insuranceCollectAtOffice') : t('booking.orderSummary.insurancePaidOnline')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="step-footer">
                  <button type="button" className="btn-ghost" onClick={() => setActiveTab('booking')}>
                    Back
                  </button>
                  <button type="button" className="btn-blue" onClick={() => setActiveTab('delivery')}>
                    Continue to Delivery
                  </button>
                </div>
              </section>

              {/* 3. Delivery */}
              <section className={activeTab === 'delivery' ? 'tab-panel active' : 'tab-panel'}>
                <div className="option-grid">
                  <div className="option-card active">
                    <strong>Deliver to customer</strong>
                    <span>Driver takes vehicle to pickup location.</span>
                  </div>
                  <div className="option-card">
                    <strong>Office pickup</strong>
                    <span>Customer collects car from branch.</span>
                  </div>
                  <div className="option-card">
                    <strong>Same return point</strong>
                    <span>Pickup and drop-off are both airport.</span>
                  </div>
                </div>
                <div className="form-grid">
                  <div className="field">
                    <label>Delivery type</label>
                    <Selection
                      className="w-full eb-select eb-select-normal"
                      list={DELIVERY_TYPE_OPTIONS}
                      title="Select delivery type"
                      value={form.deliveryType}
                      disabled={!canEditDeliveryType}
                      onChange={selected => {
                        setHasUserEditedDiscounts(true)
                        setField('deliveryType', selected?.id || '')
                      }}
                    />
                    {/* {!canEditDeliveryType && (
                      <span style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4, display: 'block' }}>Delivery type can only be changed while the booking is paid or unpaid.</span>
                    )} */}
                  </div>
                  <div className="field">
                    <label>Delivery cost</label>
                    <input className="readonly" type="number" step="0.001" value={Number(deliveryCostDisplay).toFixed(3)} readOnly />
                  </div>
                  <div className="field">
                    <label>Pickup address</label>
                    <div
                      role="button"
                      tabIndex={isPickupSelf ? -1 : 0}
                      title={isPickupSelf ? undefined : 'Click to pick on map'}
                      style={isPickupSelf ? undefined : { cursor: 'pointer' }}
                      onClick={isPickupSelf ? undefined : () => openPickupMap()}
                    >
                      <input
                        className={isPickupSelf ? 'readonly' : undefined}
                        value={form.pickupAddress}
                        readOnly
                        tabIndex={-1}
                        placeholder="Pickup address"
                        style={isPickupSelf ? undefined : { pointerEvents: 'none', cursor: 'pointer', width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>Drop-off address</label>
                    <div
                      role="button"
                      tabIndex={isPickupSelf ? -1 : 0}
                      title={isPickupSelf ? undefined : 'Click to pick on map'}
                      style={isPickupSelf ? undefined : { cursor: 'pointer' }}
                      onClick={isPickupSelf ? undefined : () => openDropoffMap()}
                    >
                      <input
                        className={isPickupSelf ? 'readonly' : undefined}
                        value={form.dropoffAddress}
                        readOnly
                        tabIndex={-1}
                        placeholder="Drop-off address"
                        style={isPickupSelf ? undefined : { pointerEvents: 'none', cursor: 'pointer', width: '100%' }}
                      />
                    </div>
                  </div>
                  <div className="field full">
                    <label>Delivery instructions</label>
                    <textarea rows={3} value={form.deliveryInstructions} onChange={e => setField('deliveryInstructions', e.target.value)} />
                  </div>
                  <div className="field full">
                    <label>Flight number (optional)</label>
                    <input
                      type="text"
                      value={form.flightNumber}
                      onChange={e => setField('flightNumber', e.target.value.toUpperCase().slice(0, 20))}
                      placeholder="e.g. WY123"
                      maxLength={20}
                      dir="ltr"
                    />
                  </div>
                </div>
                <div className="step-footer">
                  <button type="button" className="btn-ghost" onClick={() => setActiveTab('vehicle')}>
                    Back
                  </button>
                </div>
              </section>
            </div>
          </div>

          {/* Discount control center */}
          {showDiscountCard ? (
            <div className="discount-card">
              <div className="discount-header">
                <div>
                  <h2>Discount control center</h2>
                  <p>
                    {discountsReadOnly
                      ? 'Discounts applied to this booking (read-only — the booking is paid).'
                      : paidCarSwapDiscountEditable
                      ? 'Car change on a paid booking — review the discount for the new car. It is applied when you confirm the update.'
                      : 'Add discount on daily rate, delivery cost, or service fee. Source must be Ajrlee or Company.'}
                  </p>
                </div>
                {!discountsReadOnly && (
                  <button
                    type="button"
                    className="btn-blue"
                    disabled={!firstFreeTarget()}
                    style={!firstFreeTarget() ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    onClick={() => {
                      const free = firstFreeTarget()
                      if (!free) {
                        notify.error('All discount targets are already added')
                        return
                      }
                      addDiscount({ target: free.id, source: 'Ajrlee', type: 'fixed', value: 0 })
                    }}
                  >
                    + Add Discount
                  </button>
                )}
              </div>
              <div className="discount-body">
                <div className="calc-order">
                  <span>1 Rental total</span>
                  <span>2 Delivery + service</span>
                  <span>3 Component discounts</span>
                  <span>4 Total discount</span>
                  <span>5 VAT</span>
                  <span>6 Final price</span>
                </div>

                <div>
                  {discounts
                    .filter(row => !skipDeliveryForApi || (row.target !== 'delivery' && row.target !== 'service'))
                    .map(row => (
                      <div className="discount-row" key={row.id}>
                        <div className="field">
                          <label>Apply on</label>
                          <Selection
                            className="w-full eb-select eb-select-normal"
                            list={targetOptionsForRow(row)}
                            title="Select target"
                            value={row.target}
                            disabled={discountsReadOnly}
                            onChange={selected => updateDiscount(row.id, { target: selected?.id || '' })}
                          />
                        </div>
                        <div className="field">
                          <label>Source</label>
                          <Selection
                            className="w-full eb-select eb-select-normal"
                            list={sourceOptionsForRow(row)}
                            title="Select source"
                            value={row.source}
                            disabled={discountsReadOnly}
                            onChange={selected => updateDiscount(row.id, { source: selected?.id || '' })}
                          />
                        </div>
                        <div className="field">
                          <label>Type</label>
                          <Selection
                            className="w-full eb-select eb-select-normal"
                            list={DISCOUNT_TYPE_OPTIONS}
                            title="Select type"
                            value={row.type}
                            disabled={discountsReadOnly}
                            onChange={selected => updateDiscount(row.id, { type: selected?.id || '' })}
                          />
                        </div>
                        <div className="field">
                          <label>Value</label>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            className={discountsReadOnly ? 'readonly' : undefined}
                            readOnly={discountsReadOnly}
                            style={discountErrors[row.id] ? { borderColor: '#dc2626', boxShadow: '0 0 0 3px rgba(220,38,38,0.12)' } : undefined}
                            value={row.value === '' || row.value == null ? '' : row.value}
                            onChange={e => {
                              const next = e.target.value
                              updateDiscount(row.id, { value: next === '' ? '' : next })
                            }}
                          />
                        </div>
                        <div className="field">
                          <label>Budget owner</label>
                          <span className={`source-chip ${row.source === 'Ajrlee' ? 'ajrlee' : 'office'}`}>{row.source}</span>
                        </div>
                        {!discountsReadOnly && (
                          <button type="button" className="remove" title="Remove discount" aria-label="Remove discount" onClick={() => removeDiscount(row.id)}>
                            Remove
                          </button>
                        )}
                        {discountErrors[row.id] && <div className="discount-row-error">{discountErrors[row.id]}</div>}
                      </div>
                    ))}
                </div>

                <div className="note-box">
                  <div>
                    <b>Guardrail:</b> total price discount is applied after item-level discounts and before VAT, so invoice remains clean.
                  </div>
                  <span>Requires note</span>
                </div>

                <div className="panel" style={{ boxShadow: 'none', padding: '14px', borderRadius: '16px' }}>
                  <div className="panel-header" style={{ marginBottom: '12px' }}>
                    <div>
                      <h2 style={{ fontSize: '15px' }}>Reason &amp; internal note</h2>
                      <div className="hint">Shown in admin audit log, not to customer unless enabled.</div>
                    </div>
                    <span className="pill amber">Finance visible</span>
                  </div>
                  <textarea rows={3} value={form.reasonNote} onChange={e => setField('reasonNote', e.target.value)} />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Sticky live invoice */}
        <aside className="summary-card" ref={summaryRef}>
          {pricingLoading && (
            <div className="summary-loading-overlay" aria-busy="true" aria-live="polite">
              <span className="summary-spinner" role="status" aria-label="Updating prices" />
            </div>
          )}
          <div className="summary-top">
            <span>Live booking total</span>
            <h2>{money(displayFinalTotal)}</h2>
            <p>{discountAppliedInDisplay ? `Discount applied with ${discountSourceLabel} source split.` : 'Live recalculation with discount source split.'}</p>
            <div className="delta">
              <div className="delta-card">
                <small>Original total</small>
                <b>{money(displayOriginalTotal)}</b>
              </div>
            </div>
          </div>
          <div className="summary-body">
            <div className="money-row">
              <div className="left">
                <span>Rent price per day</span>
                <small>
                  {money(displayRentPerDay).replace(' OMR', '')} × {days} {days === 1 ? 'day' : 'days'}
                </small>
              </div>
              <b>{money(displayRentSubtotal)}</b>
            </div>
            <div className="money-row">
              <div className="left">
                <span>Delivery cost</span>
              </div>
              <b>{money(displayDelivery)}</b>
            </div>
            <div className="money-row">
              <div className="left">
                <span>Service fee</span>
                <small>Ajrlee platform fee</small>
              </div>
              <b>{money(displayServiceFee)}</b>
            </div>
            {extraRows.map(extra => (
              <div className="money-row" key={extra.key}>
                <div className="left">
                  <span>{t(`booking.orderSummary.extra_${extra.key}`)}</span>
                  <small>
                    {money(extra.pricePerDay).replace(' OMR', '')} × {days} {days === 1 ? 'day' : 'days'}
                  </small>
                </div>
                <b>{money(extra.amount)}</b>
              </div>
            ))}
            {insurance.type !== 'NONE' || insurance.fee > 0 ? (
              <div className="money-row">
                <div className="left">
                  <span>
                    {t('booking.orderSummary.insuranceFee')}
                    {insurance.type !== 'NONE' && ` (${insuranceTypeLabel(insurance.type)})`}
                  </span>
                  <small>
                    {insurance.collectAtOffice
                      ? `${money(insurance.amount)} — ${t('booking.orderSummary.insuranceCollectAtOffice')}`
                      : insurance.type === 'FULL'
                      ? `${money(insurance.unitPrice).replace(' OMR', '')} × ${days} ${days === 1 ? 'day' : 'days'}`
                      : t('booking.orderSummary.insurancePaidOnline')}
                  </small>
                </div>
                <b>{money(insurance.fee)}</b>
              </div>
            ) : null}
            {insuranceDroppedBySwap && (
              <div className="money-row discount">
                <div className="left">
                  <span>Insurance removed</span>
                  <small>New car doesn&apos;t offer this insurance — any insurance already paid is returned to the customer with the price difference.</small>
                </div>
              </div>
            )}

            {allDisplayBreakdown.length > 0 ? (
              allDisplayBreakdown.map(item => (
                <div className="money-row discount" key={item.id}>
                  <div className="left">
                    <span>{TARGET_LABELS[item.target]}</span>
                    <small>
                      {item.source} · {item.type === 'percent' ? `${item.value}%` : money(item.value)}
                    </small>
                  </div>
                  <b>-{money(item.amount)}</b>
                </div>
              ))
            ) : (
              <div className="money-row">
                <div className="left">
                  <span>No discount applied</span>
                  <small>Add one from the control center</small>
                </div>
                <b>0.000 OMR</b>
              </div>
            )}

            <div className="divider" />
            <div className="money-row">
              <div className="left">
                <span>Taxable after discount</span>
                <small>Before VAT</small>
              </div>
              <b>{money(displayTaxableAfter)}</b>
            </div>
            <div className="money-row">
              <div className="left">
                <span>VAT {num(vatPctForDisplay)}%</span>
                <small>On rent + delivery + extras + insurance</small>
              </div>
              <b>{money(displayVat)}</b>
            </div>
            <div className="total-row">
              <span>Customer pays</span>
              <b>{money(displayFinalTotal)}</b>
            </div>
            <div className="money-row">
              <div className="left">
                <span>Paid amount</span>
                <small>Payment status: {String(form.paymentStatus).toLowerCase()}</small>
              </div>
              <b>{money(paidAmount)}</b>
            </div>
            <div className="money-row">
              <div className="left">
                <span>Balance due</span>
                <small>After collected payment</small>
              </div>
              <b>{money(displayBalanceDue)}</b>
            </div>

            {deliveryPayable > 0 && (
              <div className="total-row" style={{ borderColor: '#fed7aa', background: '#fff7ed' }}>
                <span style={{ color: '#9a3412' }}>Payable amount</span>
                <b style={{ color: '#9a3412' }}>{money(deliveryPayable)}</b>
              </div>
            )}
            {deliveryPayable <= 0 && carSwapPayable <= 0 && existingPartialPayable > 0 && (
              <div className="total-row" style={{ borderColor: '#fed7aa', background: '#fff7ed' }}>
                <span style={{ color: '#9a3412' }}>Payable amount</span>
                <b style={{ color: '#9a3412' }}>{money(existingPartialPayable)}</b>
              </div>
            )}
            {deliveryPayable > 0 && (
              <div className="money-row">
                <div className="left">
                  <span>Delivery added</span>
                  <small>Booking becomes partial paid — customer owes the delivery difference including VAT.</small>
                </div>
              </div>
            )}
            {deliveryWalletRefund > 0 && (
              <div className="money-row discount">
                <div className="left">
                  <span>Refunded to wallet</span>
                  <small>Delivery removed — delivery cost and its VAT credited to customer wallet on save.</small>
                </div>
                <b>+{money(deliveryWalletRefund)}</b>
              </div>
            )}

            {carSwapPayable > 0 && (
              <>
                <div className="total-row" style={{ borderColor: '#fed7aa', background: '#fff7ed' }}>
                  <span style={{ color: '#9a3412' }}>Payable amount</span>
                  <b style={{ color: '#9a3412' }}>{money(carSwapPayable)}</b>
                </div>
                <div className="money-row">
                  <div className="left">
                    <span>Car changed</span>
                    <small>New car costs more than the {money(carSwapPaidTotal)} already paid — booking becomes partial paid and the customer owes the difference.</small>
                  </div>
                </div>
              </>
            )}
            {carSwapWalletRefund > 0 && (
              <div className="money-row discount">
                <div className="left">
                  <span>Refunded to wallet</span>
                  <small>New car costs less than the {money(carSwapPaidTotal)} already paid — the difference is credited to the customer wallet on save.</small>
                </div>
                <b>+{money(carSwapWalletRefund)}</b>
              </div>
            )}

            <div className="divider" />
            <div className="source-breakdown">
              <div className="money-row">
                <div className="left">
                  <span>Discount source split</span>
                  <small>Useful for settlement</small>
                </div>
              </div>
              <div className="bar-row">
                <span>Ajrlee</span>
                <div className="bar">
                  <i style={{ width: `${apiSourceSplit.ajrleePct}%` }} />
                </div>
                <b>{money(apiSourceSplit.ajrleeDiscount).replace(' OMR', '')}</b>
              </div>
              <div className="bar-row">
                <span>Company</span>
                <div className="bar office">
                  <i style={{ width: `${apiSourceSplit.officePct}%` }} />
                </div>
                <b>{money(apiSourceSplit.officeDiscount).replace(' OMR', '')}</b>
              </div>
            </div>
            <div className="audit">Every saved change writes an audit record: target, type, amount, source, admin, timestamp and reason.</div>
          </div>
          {(pricingError || stagedPricingError) && (
            <div
              className="price-unavailable-error"
              style={{ color: '#d4380d', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}
            >
              {pricingError || stagedPricingError}
            </div>
          )}
          <div className="summary-actions">
            <button type="button" className="btn-ghost" onClick={() => history.push('/bookings')}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={isSaving || dateRangeInvalid || Boolean(pricingError || stagedPricingError)}>
              {isSaving ? 'Saving…' : 'Confirm update'}
            </button>
          </div>
        </aside>
      </section>
      <ChangeCarModal />
      <PickupMapModal />
      <DropoffMapModal />
    </div>
  )
}

export default EditBookingRedesign
